// Persistent top header — rendered once by AppLayout, so it stays fixed on
// every authenticated page. Shows: avatar + user name, mobile number, a location
// switcher (all permitted locations), a menu to the secondary screens, Logout.
// Reads everything from AppContext so it works regardless of the current route.

import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar, Toolbar, Avatar, Box, Typography, Button, MenuItem,
  IconButton, Menu, ListItemIcon, ListItemText, Tooltip, Chip,
  CircularProgress, Checkbox, FormControlLabel, Autocomplete, TextField,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import EditIcon from '@mui/icons-material/Edit';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LogoutIcon from '@mui/icons-material/Logout';
import RefreshIcon from '@mui/icons-material/Refresh';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import StarIcon from '@mui/icons-material/Star';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import DashboardIcon from '@mui/icons-material/Dashboard';
import InfoIcon from '@mui/icons-material/Info';
import BusinessIcon from '@mui/icons-material/Business';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import MemoryIcon from '@mui/icons-material/Memory';
import ForumIcon from '@mui/icons-material/Forum';
import ScienceIcon from '@mui/icons-material/Science';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useApp } from '../../../core/state/AppContext';
import { arrayMove } from '../../../core/utils/reorder';
import { getAllUsers } from '../../../core/devices/deviceAdmin';
import { tokenFilter } from '../../../core/utils/search';
import { hardRefresh } from '../../../core/utils/hardRefresh';

// ── Location names arrive PREFIXED WITH THEIR OWNER ────────────────────────
// GetLocationsByUserId returns "<owner>-<LocationName>", e.g.
// "Sanjay Ailani-Factory", while device rows carry the bare name. In a narrow
// box the useful half is exactly the half that gets cut: you see the owner —
// identical on every one of your own locations — and not the site.
//
// So YOUR OWN prefix is stripped; the header already says who you are two
// inches to the left. Anything else is left whole, because for a location
// shared by someone else the owner is real information.
//
// ⚠️ THE TWO NAMES ARE IN DIFFERENT ORDERS. This header builds its name as
// Surname-first ("Ailani Sanjay") while the prefix is Name-first ("Sanjay
// Ailani"), and the user record may carry a third part the prefix omits. A
// string comparison therefore never matches and the prefix is never stripped —
// which is the whole bug. Compare TOKENS instead: strip only when every word of
// the prefix is also a word of the signed-in user's name.
//
// That token test is also what keeps a location genuinely called "Wing-A" from
// being read as owner "Wing" and site "A".
const nameTokens = (s) => String(s || '')
  .toLowerCase().split(/[^a-z0-9]+/i).filter(Boolean);

function splitLocationName(raw, fullName, keepOwner) {
  const text = String(raw || '').trim();
  // With ALL locations listed, the owner is the only thing telling a dozen
  // sites called "Home" apart — so nothing is stripped, including your own.
  // Stripping only yours would leave one row shorter than the rest and looking
  // like a different kind of thing.
  if (keepOwner) return { owner: '', site: text };
  const dash = text.indexOf('-');
  if (dash <= 0) return { owner: '', site: text };

  const prefix = text.slice(0, dash).trim();
  const site = text.slice(dash + 1).trim();
  if (!prefix || !site) return { owner: '', site: text };

  const mine = nameTokens(fullName);
  const parts = nameTokens(prefix);
  const isMe = parts.length > 0 && parts.every((t) => mine.includes(t));

  // Mine -> drop the prefix. Not mine -> show the name exactly as stored,
  // rather than guessing that the hyphen separates an owner at all.
  return isMe ? { owner: '', site } : { owner: '', site: text };
}

export default function DesktopHeader() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const {
    user, locations, locationId, setLocationId, logout, showCommon, setShowCommon,
    isSuperAdmin, canTuyaTest, companyName, canReorder, editOrder, setEditOrder, reorderLocations,
    viewAsUser, setViewAsUser, allLocations, setAllLocations,
  } = useApp();

  // Admin testing controls. Loaded lazily — 450+ users is not worth fetching
  // for the 99% of sessions that never open this.
  const [allUsers, setAllUsers] = useState([]);
  useEffect(() => {
    if (!isSuperAdmin || allUsers.length) return;
    getAllUsers().then((res) => setAllUsers(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  }, [isSuperAdmin, allUsers.length]);
  const [anchor, setAnchor] = useState(null);
  const [locMenu, setLocMenu] = useState(null);
  const [dragI, setDragI] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const onHome = pathname === '/home';
  const onFav = pathname === '/favourites';
  const activeSx = (active) => ({ bgcolor: active ? 'rgba(255,255,255,0.18)' : 'transparent' });

  const fullName = user
    ? [user.Surname, user.Name, user.FatherHusband].filter(Boolean).join(' ')
    : 'User';
  const initials = (fullName || 'U').trim().charAt(0).toUpperCase();

  const go = (path) => { setAnchor(null); navigate(path); };
  const onLogout = () => { logout(); navigate('/login', { replace: true }); };

  // The spinner is not decoration: clearing caches is asynchronous, so there is
  // a real gap before the page goes, and without it a second click lands during
  // that gap. It is never cleared — the document navigates away instead.
  const onHardRefresh = () => {
    setRefreshing(true);
    hardRefresh().catch(() => { setRefreshing(false); });
  };

  return (
    <AppBar position="static" elevation={2}>
      {/* `dense` plus explicit type sizes below: the header carried MUI's
          default 64px bar and 1rem body text, which on a control screen is
          heavier than anything it labels. */}
      <Toolbar variant="dense" sx={{ gap: 1.25, minHeight: 56 }}>
        <IconButton color="inherit" edge="start" onClick={(e) => setAnchor(e.currentTarget)}>
          <MenuIcon />
        </IconButton>
        <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
          <MenuItem onClick={() => go('/home')}>
            <ListItemIcon><HomeIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Home</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => go('/favourites')}>
            <ListItemIcon><StarIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Favourites</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => go('/scenes')}>
            <ListItemIcon><GroupWorkIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Scenes</ListItemText>
          </MenuItem>
          {/* ONE entry. Wall Mode used to be three — run it, design it, switch
              the tablet into it — which is three names for one feature and no
              hint that they belong together. The setup page carries all of it:
              the grid, the switch, and a button that opens the panel. */}
          <MenuItem onClick={() => go('/wall/setup')}>
            <ListItemIcon><DashboardIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Wall Panel</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => go('/about')}>
            <ListItemIcon><InfoIcon fontSize="small" /></ListItemIcon>
            <ListItemText>About</ListItemText>
          </MenuItem>
          {isSuperAdmin && (
            <MenuItem onClick={() => go('/companies')}>
              <ListItemIcon><BusinessIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Companies (Admin)</ListItemText>
            </MenuItem>
          )}
          {isSuperAdmin && (
            <MenuItem onClick={() => go('/device-admin')}>
              <ListItemIcon><MemoryIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Device by Unique Number (Admin)</ListItemText>
            </MenuItem>
          )}
          {isSuperAdmin && (
            <MenuItem onClick={() => go('/user-access')}>
              <ListItemIcon><PersonAddAlt1Icon fontSize="small" /></ListItemIcon>
              <ListItemText>User Access (Admin)</ListItemText>
            </MenuItem>
          )}
          {isSuperAdmin && (
            <MenuItem onClick={() => go('/device-types')}>
              <ListItemIcon><MemoryIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Device Types (Admin)</ListItemText>
            </MenuItem>
          )}
          {isSuperAdmin && (
            <MenuItem onClick={() => go('/mqtt')}>
              <ListItemIcon><ForumIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Message Box (Admin)</ListItemText>
            </MenuItem>
          )}
          {/* AllowTestingTuya, not IsSuperAdmin — the bench operators who are
              granted this in the backend are not Super Admins. */}
          {canTuyaTest && (
            <MenuItem onClick={() => go('/tuya-testing')}>
              <ListItemIcon><ScienceIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Tuya Testing (Admin)</ListItemText>
            </MenuItem>
          )}
          {isSuperAdmin && (
            <MenuItem onClick={() => go('/firmware')}>
              <ListItemIcon><SystemUpdateAltIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Firmware (Admin)</ListItemText>
            </MenuItem>
          )}
          {/* No BLE Test entry — a desktop browser cannot drive Bluetooth, so
              the page could only say so. It lives in the mobile shell's Me tab. */}
        </Menu>

        {/* Quick nav */}
        <Tooltip title="Home">
          <IconButton color="inherit" onClick={() => navigate('/home')} sx={activeSx(onHome)}>
            <HomeIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Favourites">
          <IconButton color="inherit" onClick={() => navigate('/favourites')} sx={activeSx(onFav)}>
            <StarIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title="Profile">
          <Box
            onClick={() => navigate('/profile')}
            sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', minWidth: 0 }}
          >
            <Avatar sx={{ width: 32, height: 32, fontSize: 15, bgcolor: 'primary.dark', color: 'primary.contrastText' }}>
              {initials || <PersonIcon />}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Typography fontWeight={700} noWrap sx={{ fontSize: 14 }}>{fullName}</Typography>
                {isSuperAdmin && (
                  <Chip
                    label="Super Admin" size="small" color="secondary"
                    sx={{ height: 16, fontSize: '0.58rem', fontWeight: 700 }}
                  />
                )}
              </Box>
              <Typography variant="caption" sx={{ opacity: 0.8, fontSize: 11 }} noWrap>
                {user?.MobileNumber}{user?.UserId != null ? ` · ID ${user.UserId}` : ''}
              </Typography>
            </Box>
          </Box>
        </Tooltip>

        {companyName && (
          <Tooltip title={isSuperAdmin ? 'Manage Company Info' : ''}>
            <Box
              onClick={isSuperAdmin ? () => navigate('/companies') : undefined}
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.6, ml: 0.5, px: 1, py: 0.25,
                borderRadius: 2, bgcolor: 'rgba(255,255,255,0.12)',
                cursor: isSuperAdmin ? 'pointer' : 'default',
              }}
            >
              <BusinessIcon fontSize="small" />
              <Typography fontWeight={700} noWrap sx={{ fontSize: 13 }}>{companyName}</Typography>
              {isSuperAdmin && <EditIcon sx={{ fontSize: 15, opacity: 0.85 }} />}
            </Box>
          </Tooltip>
        )}

        <Box sx={{ flex: 1 }} />

        {/* Edit-order toggle (Super Admin or location owner). */}
        {canReorder && (
          <Tooltip title={editOrder ? 'Finish reordering' : 'Edit order of rooms, devices & locations'}>
            <Button
              size="small"
              variant={editOrder ? 'contained' : 'outlined'}
              color={editOrder ? 'secondary' : 'inherit'}
              startIcon={<SwapVertIcon />}
              onClick={() => setEditOrder((v) => !v)}
              sx={{ whiteSpace: 'nowrap', borderColor: 'rgba(255,255,255,0.5)' }}
            >
              {editOrder ? 'Done' : 'Edit order'}
            </Button>
          </Tooltip>
        )}

        {/* Reorder locations (only while editing order). */}
        {canReorder && editOrder && (
          <>
            <Tooltip title="Reorder locations">
              <IconButton color="inherit" onClick={(e) => setLocMenu(e.currentTarget)}>
                <DragIndicatorIcon />
              </IconButton>
            </Tooltip>
            <Menu anchorEl={locMenu} open={!!locMenu} onClose={() => setLocMenu(null)}>
              <MenuItem disabled sx={{ opacity: 0.8 }}>
                <ListItemText primaryTypographyProps={{ variant: 'caption' }}>
                  Drag to reorder locations
                </ListItemText>
              </MenuItem>
              {locations.map((l, i) => (
                <MenuItem
                  key={l.LocationID}
                  draggable
                  onDragStart={(e) => { setDragI(i); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(i)); }}
                  onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (dragI != null && dragI !== i) reorderLocations(arrayMove(locations, dragI, i));
                    setDragI(null);
                  }}
                  sx={{ cursor: 'grab' }}
                >
                  <ListItemIcon><DragIndicatorIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>{l.LocationName || `Location ${l.LocationID}`}</ListItemText>
                  <Box sx={{ display: 'flex' }} onClick={(e) => e.stopPropagation()}>
                    <IconButton size="small" disabled={i === 0} onClick={() => reorderLocations(arrayMove(locations, i, i - 1))}><KeyboardArrowUpIcon fontSize="small" /></IconButton>
                    <IconButton size="small" disabled={i === locations.length - 1} onClick={() => reorderLocations(arrayMove(locations, i, i + 1))}><KeyboardArrowDownIcon fontSize="small" /></IconButton>
                  </Box>
                </MenuItem>
              ))}
            </Menu>
          </>
        )}

        {/* Common Devices toggle (Home only) — green = shown, red = hidden. */}
        {onHome && (
          <Tooltip title={showCommon ? 'Hide common devices' : 'Show common devices'}>
            <Button
              size="small"
              variant="contained"
              color={showCommon ? 'success' : 'error'}
              startIcon={<GroupWorkIcon />}
              onClick={() => setShowCommon((v) => !v)}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Common Devices
            </Button>
          </Tooltip>
        )}

        {/* Location switcher — TYPE TO SEARCH.
            It was a plain Select, which is fine for the four locations a normal
            user has and unusable for the 331 an admin sees with "All locations"
            on: no typing, just a very long scroll. Word-order-independent, so
            "home sanjay" finds "Sanjay Ailani-Home". */}
        <Autocomplete
          size="small"
          sx={{ width: 250 }}
          options={locations}
          value={locations.find((l) => String(l.LocationID) === String(locationId)) || null}
          onChange={(_, v) => v && setLocationId(v.LocationID)}
          getOptionLabel={(l) => splitLocationName(
            l.LocationName || `Location ${l.LocationID}`, fullName, allLocations,
          ).site}
          isOptionEqualToValue={(o, v) => String(o.LocationID) === String(v.LocationID)}
          // Searches the name AS STORED, so the owner is findable even when the
          // prefix is hidden from the label.
          filterOptions={tokenFilter((l) => `${l.LocationName || ''} ${l.MasterUserName || ''}`)}
          renderOption={(props, l) => (
            <li {...props} key={l.LocationID}>
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {splitLocationName(l.LocationName || '', fullName, allLocations).site}
                </Typography>
                {allLocations && l.MasterUserName && (
                  <Typography variant="caption" color="text.secondary">
                    {l.MasterUserName}
                  </Typography>
                )}
              </Box>
            </li>
          )}
          renderInput={(p) => (
            <TextField
              {...p}
              placeholder="Type to search a location…"
              InputProps={{
                ...p.InputProps,
                startAdornment: <LocationOnIcon sx={{ fontSize: 18, mr: 0.5 }} />,
              }}
              sx={{
                '& .MuiInputBase-root': { color: 'inherit', fontSize: 13, bgcolor: 'rgba(255,255,255,0.10)', borderRadius: 2 },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.35)' },
                '& .MuiSvgIcon-root': { color: 'inherit' },
              }}
            />
          )}
        />

        {/* Reloads the app past the caches — see core/utils/hardRefresh for why
            a plain reload() is not enough. Icon only: it sits beside Logout and
            the two must not read as a pair of equally weighted choices. */}
        <Tooltip title="Hard refresh — reload the app and clear cached files">
          <span>
            <IconButton
              size="small"
              color="inherit"
              disabled={refreshing}
              onClick={onHardRefresh}
            >
              {refreshing
                ? <CircularProgress size={18} color="inherit" />
                : <RefreshIcon sx={{ fontSize: 20 }} />}
            </IconButton>
          </span>
        </Tooltip>

        {/* ── Admin testing ────────────────────────────────────────────────
            Two switches that change WHOSE data is shown. Both are deliberately
            in the header rather than a settings page: they must be visible
            while they are on, because everything else on screen is a lie about
            whose house it is. */}
        {isSuperAdmin && (
          <>
            <Tooltip title="Show every location on the server, with all of its rooms and devices — mappings ignored">
              <FormControlLabel
                sx={{ mr: 0 }}
                control={(
                  <Checkbox
                    size="small" checked={allLocations}
                    onChange={(e) => setAllLocations(e.target.checked)}
                    sx={{ color: 'inherit', '&.Mui-checked': { color: 'inherit' } }}
                  />
                )}
                label={<Typography sx={{ fontSize: 11 }}>All locations</Typography>}
              />
            </Tooltip>

            {/* The two lenses do NOT stack. All Locations already drops every
                user mapping, so a name picked here would have nothing left to
                narrow — and asking for "this user's rooms" in someone else's
                location is what returned nothing and printed "No rooms". */}
            <Autocomplete
              size="small"
              sx={{ width: 200 }}
              disabled={allLocations}
              options={allUsers}
              value={allLocations ? null : viewAsUser}
              onChange={(_, v) => setViewAsUser(v)}
              getOptionLabel={(u) => [u.Surname, u.Name, u.FatherHusband]
                .filter(Boolean).join(' ').trim() || `User ${u.UserId}`}
              isOptionEqualToValue={(o, v) => String(o.UserId) === String(v.UserId)}
              filterOptions={tokenFilter((u) => `${u.Surname || ''} ${u.Name || ''} ${u.FatherHusband || ''} ${u.MobileNumber || ''}`)}
              renderInput={(p) => (
                <TextField
                  {...p}
                  placeholder={allLocations ? 'Ignored — all locations' : 'Type a name or mobile…'}
                  sx={{
                    '& .MuiInputBase-root': { color: 'inherit', fontSize: 12 },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.35)' },
                    '& .MuiSvgIcon-root': { color: 'inherit' },
                  }}
                />
              )}
            />
          </>
        )}

        <Tooltip title="Logout">
          <Button size="small" color="inherit" startIcon={<LogoutIcon />} onClick={onLogout}>
            Logout
          </Button>
        </Tooltip>
      </Toolbar>

      {/* Impersonation must never be invisible: everything below is someone
          else's data, and an admin who forgets will "fix" the wrong house. */}
      {allLocations ? (
        <Box sx={{
          bgcolor: 'warning.main', color: 'warning.contrastText',
          px: 2, py: 0.25, display: 'flex', alignItems: 'center', gap: 1,
        }}
        >
          <Typography sx={{ fontSize: 12, fontWeight: 700, flex: 1 }}>
            All locations — every room and device is shown, whoever owns it.
            User mappings are ignored.
          </Typography>
          <Button size="small" color="inherit" onClick={() => setAllLocations(false)} sx={{ fontSize: 11 }}>
            Back to me
          </Button>
        </Box>
      ) : viewAsUser && (
        <Box sx={{
          bgcolor: 'warning.main', color: 'warning.contrastText',
          px: 2, py: 0.25, display: 'flex', alignItems: 'center', gap: 1,
        }}
        >
          <Typography sx={{ fontSize: 12, fontWeight: 700, flex: 1 }}>
            {`Viewing as ${[viewAsUser.Surname, viewAsUser.Name].filter(Boolean).join(' ')} · ID ${viewAsUser.UserId} — this is not your account`}
          </Typography>
          <Button size="small" color="inherit" onClick={() => setViewAsUser(null)} sx={{ fontSize: 11 }}>
            Back to me
          </Button>
        </Box>
      )}
    </AppBar>
  );
}
